import type { BoardData } from '../model/schema.js';
import { topicOfAnchor } from './topics.js';
import type { CommentLike, OpenItemLike, PersonRef, ThreadChange } from './types.js';

const MAX_LABEL = 80;

function samePerson(a: PersonRef, b: PersonRef): boolean {
  return a.kind === b.kind && a.id === b.id;
}

function labelOf(text: string): string {
  return text.trim().split('\n', 1)[0]!.trim().slice(0, MAX_LABEL);
}

function newer(a: string, b: string): boolean {
  return Date.parse(a) > Date.parse(b);
}

function latest(a: string | null, b: string): string {
  return a === null || newer(b, a) ? b : a;
}

function byRecency(x: ThreadChange, y: ThreadChange): number {
  return Date.parse(y.at) - Date.parse(x.at) || (x.key < y.key ? -1 : 1);
}

/** Root comment id of every comment (parent chain; orphans and cycles end at themselves). */
export function threadRoots(comments: readonly CommentLike[]): Map<string, string> {
  const byId = new Map(comments.map((c) => [c.id, c]));
  const roots = new Map<string, string>();
  for (const c of comments) {
    let cur = c;
    const seen = new Set<string>([cur.id]);
    while (cur.parentId !== null) {
      const parent = byId.get(cur.parentId);
      if (parent === undefined || seen.has(parent.id)) break;
      seen.add(parent.id);
      cur = parent;
    }
    roots.set(c.id, cur.id);
  }
  return roots;
}

/** Comments grouped by thread root id (root first). */
export function commentThreads(comments: readonly CommentLike[]): Map<string, CommentLike[]> {
  const roots = threadRoots(comments);
  const threads = new Map<string, CommentLike[]>();
  for (const c of comments) {
    const root = roots.get(c.id)!;
    const list = threads.get(root) ?? [];
    if (c.id === root) list.unshift(c);
    else list.push(c);
    threads.set(root, list);
  }
  return threads;
}

/** Comment threads with activity of other people after `since` (decision C1: by timestamp). */
export function commentChanges(
  comments: readonly CommentLike[],
  since: string,
  me: PersonRef,
  data: BoardData,
): ThreadChange[] {
  const out: ThreadChange[] = [];
  for (const [rootId, thread] of commentThreads(comments)) {
    const root = thread[0]!;
    let at: string | null = null;
    for (const c of thread) {
      if (samePerson(c.author, me)) continue;
      if (newer(c.createdAt, since)) at = latest(at, c.createdAt);
      if (c.editedAt !== null && newer(c.editedAt, since)) at = latest(at, c.editedAt);
    }
    if (at === null) continue;
    out.push({
      type: 'comments',
      key: `comment:${rootId}`,
      id: rootId,
      kind: newer(root.createdAt, since) ? 'added' : 'changed',
      topic: topicOfAnchor(data, root.anchor),
      anchor: root.anchor,
      label: labelOf(root.body),
      at,
    });
  }
  return out.sort(byRecency);
}

/** Open items created / updated / replied to after `since`, not counting my own activity. */
export function openItemChanges(
  items: readonly OpenItemLike[],
  since: string,
  me: PersonRef,
  data: BoardData,
): ThreadChange[] {
  const out: ThreadChange[] = [];
  for (const item of items) {
    let at: string | null = null;
    for (const r of item.replies ?? [])
      if (!samePerson(r.author, me) && newer(r.createdAt, since)) at = latest(at, r.createdAt);
    if (item.history) {
      // edits by other people only (my own status change is no news to me, OIL-186)
      for (const e of item.history)
        if (!samePerson(e.actor, me) && newer(e.createdAt, since)) at = latest(at, e.createdAt);
    } else {
      const mineOnly = samePerson(item.originator, me) && item.updatedAt === item.createdAt;
      if (newer(item.updatedAt, since) && !mineOnly) at = latest(at, item.updatedAt);
    }
    if (at === null) continue;
    out.push({
      type: 'openitems',
      key: `openitem:${item.id}`,
      id: item.id,
      kind: newer(item.createdAt, since) ? 'added' : 'changed',
      topic: item.topic ?? topicOfAnchor(data, item.anchor),
      anchor: item.anchor,
      label: labelOf(item.title),
      at,
    });
  }
  return out.sort(byRecency);
}
